import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { authService, expenseNotesService } from '../services/api';

const initialForm = {
  date: new Date().toISOString().split('T')[0],
  category: 'Repas',
  description: '',
  amountHT: '',
  vatRate: 20,
  paymentMethod: 'Carte',
  status: 'DRAFT',
  notes: ''
};

const statusOptions = [
  { value: 'DRAFT', label: 'Brouillon' },
  { value: 'SUBMITTED', label: 'Soumise' },
  { value: 'APPROVED', label: 'Validée' },
  { value: 'REJECTED', label: 'Refusée' }
];

const categoryOptions = ['Repas', 'Transport', 'Hébergement', 'Fournitures', 'Autre'];

const getStatusLabel = (status) => {
  const map = {
    DRAFT: 'Brouillon',
    SUBMITTED: 'Soumise',
    APPROVED: 'Validée',
    REJECTED: 'Refusée',
    PENDING: 'En attente',
    REIMBURSED: 'Remboursée'
  };
  return map[status] || status;
};

const getDecisionActionLabel = (action) => {
  if (action === 'EXPENSE_APPROVED') return 'Validée';
  if (action === 'EXPENSE_REJECTED') return 'Refusée';
  return action || '';
};

const ExpenseNotes = () => {
  const currentUser = authService.getCurrentUserFromStorage();
  const isAdmin = currentUser?.role === 'ADMIN';
  const teamRole = currentUser?.teamRole || 'OWNER';
  const canApproveReject = isAdmin || ['OWNER', 'MANAGER'].includes(teamRole);
  const canManageDraftAndSubmit = isAdmin || ['OWNER', 'MANAGER', 'MEMBER'].includes(teamRole);

  const [notes, setNotes] = useState([]);
  const [stats, setStats] = useState({
    summary: { count: 0, totalHT: 0, totalVAT: 0, totalTTC: 0 },
    byStatus: [],
    byCategory: [],
    monthly: []
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditNoteLabel, setAuditNoteLabel] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });

  const loadNotes = useCallback(async (overrideFilters = filters) => {
    setLoading(true);
    try {
      const data = await expenseNotesService.getAll(overrideFilters);
      setNotes(data.notes || []);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 403) {
        setMessage({ type: 'error', text: 'Fonctionnalité réservée au plan PRO actif' });
      } else {
        setMessage({ type: 'error', text: error?.response?.data?.error || 'Impossible de charger les notes de frais' });
      }
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadStats = useCallback(async (overrideFilters = filters) => {
    try {
      const data = await expenseNotesService.getStats(overrideFilters);
      setStats({
        summary: data.summary || { count: 0, totalHT: 0, totalVAT: 0, totalTTC: 0 },
        byStatus: data.byStatus || [],
        byCategory: data.byCategory || [],
        monthly: data.monthly || []
      });
    } catch (error) {
      // silencieux pour ne pas écraser les messages principaux
    }
  }, [filters]);

  useEffect(() => {
    loadNotes();
    loadStats();
  }, [loadNotes, loadStats]);

  const resetForm = () => {
    setEditingId(null);
    setForm(initialForm);
  };

  const handleOpenCreate = () => {
    if (!canManageDraftAndSubmit) {
      setMessage({ type: 'error', text: 'Permissions insuffisantes pour créer une note de frais' });
      return;
    }
    resetForm();
    setDialogOpen(true);
  };

  const handleOpenEdit = (note) => {
    if (!canApproveReject && ['APPROVED', 'REJECTED'].includes(note.status)) {
      setMessage({ type: 'error', text: 'Cette note validée/refusée ne peut pas être modifiée par votre rôle' });
      return;
    }
    setEditingId(note.id);
    setForm({
      date: new Date(note.date).toISOString().split('T')[0],
      category: note.category,
      description: note.description,
      amountHT: note.amountHT,
      vatRate: note.vatRate,
      paymentMethod: note.paymentMethod || 'Carte',
      status: note.status,
      notes: note.notes || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const amountHT = Number(form.amountHT);
    if (!form.description || Number.isNaN(amountHT) || amountHT <= 0) {
      setMessage({ type: 'error', text: 'Description et montant HT valide requis' });
      return;
    }

    const payload = {
      ...form,
      amountHT,
      vatRate: Number(form.vatRate)
    };

    if (!canApproveReject && ['APPROVED', 'REJECTED'].includes(payload.status)) {
      setMessage({ type: 'error', text: 'Seuls un propriétaire ou manager peuvent valider/refuser une note' });
      return;
    }

    try {
      if (editingId) {
        await expenseNotesService.update(editingId, payload);
        setMessage({ type: 'success', text: 'Note de frais mise à jour' });
      } else {
        await expenseNotesService.create(payload);
        setMessage({ type: 'success', text: 'Note de frais ajoutée' });
      }
      setDialogOpen(false);
      resetForm();
      await loadNotes();
      await loadStats();
    } catch (error) {
      setMessage({ type: 'error', text: error?.response?.data?.error || 'Erreur lors de la sauvegarde' });
    }
  };

  const handleDelete = async (id) => {
    if (!canApproveReject) {
      setMessage({ type: 'error', text: 'Seuls un propriétaire ou manager peuvent supprimer une note' });
      return;
    }
    if (!window.confirm('Supprimer cette note de frais ?')) return;
    try {
      await expenseNotesService.delete(id);
      setMessage({ type: 'success', text: 'Note de frais supprimée' });
      await loadNotes();
      await loadStats();
    } catch (error) {
      setMessage({ type: 'error', text: error?.response?.data?.error || 'Erreur lors de la suppression' });
    }
  };

  const handleExport = async () => {
    try {
      const blob = await expenseNotesService.exportCsv(filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `notes-de-frais-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de l\'export CSV' });
    }
  };

  const handleExportExcel = async () => {
    try {
      const blob = await expenseNotesService.exportExcel(filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `notes-de-frais-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de l\'export Excel' });
    }
  };

  const handleApplyFilters = async () => {
    await loadNotes(filters);
    await loadStats(filters);
  };

  const handleResetFilters = async () => {
    const reset = { category: '', status: '', dateFrom: '', dateTo: '' };
    setFilters(reset);
    await loadNotes(reset);
    await loadStats(reset);
  };

  const handleSetStatus = async (note, status) => {
    try {
      await expenseNotesService.update(note.id, { status });
      setMessage({ type: 'success', text: `Note ${status === 'APPROVED' ? 'validée' : 'refusée'}` });
      await loadNotes(filters);
      await loadStats(filters);
    } catch (error) {
      setMessage({ type: 'error', text: error?.response?.data?.error || 'Erreur lors de la mise à jour du statut' });
    }
  };

  const getAuditActionLabel = (action) => {
    if (action === 'EXPENSE_APPROVED') return 'Validée';
    if (action === 'EXPENSE_REJECTED') return 'Refusée';
    return action;
  };

  const handleOpenAudit = async (note) => {
    setAuditDialogOpen(true);
    setAuditLoading(true);
    setAuditEntries([]);
    setAuditNoteLabel(note.description || note.category || 'Note de frais');
    try {
      const data = await expenseNotesService.getAudit(note.id);
      setAuditEntries(data.entries || []);
    } catch (error) {
      setMessage({ type: 'error', text: error?.response?.data?.error || 'Impossible de charger l\'historique' });
    } finally {
      setAuditLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Notes de frais</Typography>
          <Typography variant="body2" color="text.secondary">Suivez et exportez vos dépenses professionnelles</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportExcel}>Exporter Excel</Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport}>Exporter CSV</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>Nouvelle note</Button>
        </Box>
      </Box>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary">Total notes de frais</Typography>
              <Typography variant="h5" fontWeight={700}>{stats.summary.count}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary">Montant total TTC</Typography>
              <Typography variant="h5" fontWeight={700}>{stats.summary.totalTTC.toFixed(2)} €</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary">Total TVA</Typography>
              <Typography variant="h5" fontWeight={700}>{stats.summary.totalVAT.toFixed(2)} €</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Statut</InputLabel>
                <Select
                  value={filters.status}
                  label="Statut"
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="">Tous</MenuItem>
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Catégorie</InputLabel>
                <Select
                  value={filters.category}
                  label="Catégorie"
                  onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                >
                  <MenuItem value="">Toutes</MenuItem>
                  {categoryOptions.map((category) => (
                    <MenuItem key={category} value={category}>{category}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Du"
                value={filters.dateFrom}
                onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Au"
                value={filters.dateTo}
                onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" onClick={handleApplyFilters}>Filtrer</Button>
                <Button variant="text" onClick={handleResetFilters}>Reset</Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Répartition par statut</Typography>
              {stats.byStatus.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Aucune donnée</Typography>
              ) : stats.byStatus.map((row) => (
                <Box key={row.status} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">{row.label}</Typography>
                  <Typography variant="body2" fontWeight={600}>{row.count} • {Number(row.totalTTC).toFixed(2)} €</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Top catégories</Typography>
              {stats.byCategory.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Aucune donnée</Typography>
              ) : stats.byCategory.slice(0, 5).map((row) => (
                <Box key={row.category} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">{row.category}</Typography>
                  <Typography variant="body2" fontWeight={600}>{row.count} • {Number(row.totalTTC).toFixed(2)} €</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Dépenses mensuelles</Typography>
              {stats.monthly.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Aucune donnée</Typography>
              ) : stats.monthly.slice(-5).map((row) => (
                <Box key={row.month} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">{row.month}</Typography>
                  <Typography variant="body2" fontWeight={600}>{row.count} • {Number(row.totalTTC).toFixed(2)} €</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Catégorie</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">HT</TableCell>
              <TableCell align="right">TVA</TableCell>
              <TableCell align="right">TTC</TableCell>
              <TableCell>Paiement</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Dernière validation</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {notes.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{new Date(item.date).toLocaleDateString('fr-FR')}</TableCell>
                <TableCell>{item.category}</TableCell>
                <TableCell>{item.description}</TableCell>
                <TableCell align="right">{Number(item.amountHT).toFixed(2)} €</TableCell>
                <TableCell align="right">{Number(item.vatAmount).toFixed(2)} €</TableCell>
                <TableCell align="right">{Number(item.totalTTC).toFixed(2)} €</TableCell>
                <TableCell>{item.paymentMethod || '-'}</TableCell>
                <TableCell>{getStatusLabel(item.status)}</TableCell>
                <TableCell>
                  {item.lastDecision ? (
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{item.lastDecision.userName || 'Utilisateur'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getDecisionActionLabel(item.lastDecision.action)} • {new Date(item.lastDecision.timestamp).toLocaleString('fr-FR')}
                      </Typography>
                    </Box>
                  ) : '-'}
                </TableCell>
                <TableCell align="center">
                  <IconButton onClick={() => handleOpenAudit(item)} title="Historique"><HistoryIcon /></IconButton>
                  <IconButton onClick={() => handleOpenEdit(item)}><EditIcon /></IconButton>
                  {canApproveReject && item.status === 'SUBMITTED' && (
                    <>
                      <Button size="small" onClick={() => handleSetStatus(item, 'APPROVED')}>Valider</Button>
                      <Button size="small" color="warning" onClick={() => handleSetStatus(item, 'REJECTED')}>Refuser</Button>
                    </>
                  )}
                  {canApproveReject && <IconButton color="error" onClick={() => handleDelete(item.id)}><DeleteIcon /></IconButton>}
                </TableCell>
              </TableRow>
            ))}
            {!loading && notes.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">Aucune note de frais</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Modifier la note de frais' : 'Nouvelle note de frais'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Date"
                value={form.date}
                onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Catégorie</InputLabel>
                <Select
                  value={form.category}
                  label="Catégorie"
                  onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
                >
                  {categoryOptions.map((category) => (
                    <MenuItem key={category} value={category}>{category}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Montant HT"
                value={form.amountHT}
                onChange={(e) => setForm(prev => ({ ...prev, amountHT: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="TVA %"
                value={form.vatRate}
                onChange={(e) => setForm(prev => ({ ...prev, vatRate: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Statut</InputLabel>
                <Select
                  value={form.status}
                  label="Statut"
                  onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))}
                >
                  {statusOptions
                    .filter((option) => canApproveReject || !['APPROVED', 'REJECTED'].includes(option.value))
                    .map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Méthode de paiement</InputLabel>
                <Select
                  value={form.paymentMethod}
                  label="Méthode de paiement"
                  onChange={(e) => setForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
                >
                  <MenuItem value="Carte">Carte</MenuItem>
                  <MenuItem value="Virement">Virement</MenuItem>
                  <MenuItem value="Espèces">Espèces</MenuItem>
                  <MenuItem value="Autre">Autre</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                minRows={2}
                value={form.notes}
                onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleSave} disabled={!canManageDraftAndSubmit}>{editingId ? 'Mettre à jour' : 'Créer'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={auditDialogOpen} onClose={() => setAuditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Historique d'audit — {auditNoteLabel}</DialogTitle>
        <DialogContent>
          {auditLoading ? (
            <Typography>Chargement...</Typography>
          ) : auditEntries.length === 0 ? (
            <Typography color="text.secondary">Aucune validation/refus enregistrée.</Typography>
          ) : (
            <Box>
              {auditEntries.map((entry) => (
                <Box key={entry.id} sx={{ py: 1.2, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                  <Typography variant="body2" fontWeight={700}>{getAuditActionLabel(entry.action)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Par {entry.userName || 'Utilisateur'} le {new Date(entry.timestamp).toLocaleString('fr-FR')}
                  </Typography>
                  {entry?.changes?.fromStatus || entry?.changes?.toStatus ? (
                    <Typography variant="caption" color="text.secondary">
                      {entry.changes.fromStatus ? getStatusLabel(entry.changes.fromStatus) : '—'} → {entry.changes.toStatus ? getStatusLabel(entry.changes.toStatus) : '—'}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpenseNotes;
